import { initTableSelectionOnLoad } from 'owa-mail-actions';
import loadInitialRowsFromServer from './loadInitialRowsFromServer';
import updateTablesInCache from '../helpers/tableCache/updateTablesInCache';
import { ActionSource } from 'owa-mail-store';
import {
    MailFolderTableQuery,
    TableQueryType,
    TableView,
    isFolderPaused,
} from 'owa-mail-list-store';
import { MAX_CACHED_ROWS_TO_SHOW_TABLE_LOAD } from '../data-loading/TableLoadingConstants';
import { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';

/**
 * Performs the first load for a mail table
 * @param tableView - the given mail tableView
 * @param onInitialTableLoadComplete - callback for when the initial table load has completed
 * @param actionSource action that initiated the switch folder action
 * @return a promise that resolves when the load initial table has completed either from server or from cache
 */
export default function loadInitialMailTable(
    tableView: TableView,
    onInitialTableLoadComplete: OnInitialTableLoadComplete,
    actionSource: ActionSource
): Promise<void> {
    // load table from cache by setting the currentLoadedIndex
    tableView.currentLoadedIndex = Math.min(
        MAX_CACHED_ROWS_TO_SHOW_TABLE_LOAD,
        tableView.rowKeys.length
    );

    // Init table selection on any cached items we have
    initTableSelectionOnLoad(tableView);

    const isTableViewOfFolderType = tableView.tableQuery.type == TableQueryType.Folder;

    // Update tables in cache
    updateTablesInCache(tableView);

    if (
        isTableViewOfFolderType &&
        (actionSource === 'FolderNodeClick' ||
            actionSource === 'Keyboard' ||
            actionSource === 'FavoritesPersonaNode')
    ) {
        const datapoint = returnTopExecutingActionDatapoint();
        if (datapoint) {
            datapoint.addCustomProperty(
                'isCached',
                tableView.rowKeys.length > 0 /* tableHasCachedItems */
            );
            datapoint.addCustomProperty(
                'scenario',
                (tableView.tableQuery as MailFolderTableQuery).scenarioType
            );
        }
    }

    // If folder is paused and table has already been loaded, do not load rows
    if (
        isTableViewOfFolderType &&
        tableView.isInitialLoadComplete &&
        isFolderPaused(tableView.tableQuery.folderId)
    ) {
        return Promise.resolve();
    }

    const loadFromServerPromise = loadInitialRowsFromServer(
        tableView,
        false /* isTablePrefetched */,
        onInitialTableLoadComplete
    );

    // Resolve the initial load promise immediately if we're showing cached items, otherwise resolve when the server returns results
    return tableView.isInitialLoadComplete ? Promise.resolve() : loadFromServerPromise;
}
